import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Loader } from '../../reusable/components/loader';
import { useNavigate, useParams } from 'react-router-dom';
import { BlogSummary } from '../../models/BlogSummary';
import { 
  collection, 
  query, 
  getDocs, 
  where, 
  orderBy, 
  limit, 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { db } from '../../config/firebase-config';
import './BlogPage.css';

// Optimized blog details fetching
const fetchBlogDetailsOptimized = async (blogId: string) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    const blogSnap = await getDoc(blogRef);
    
    if (!blogSnap.exists()) {
      return null;
    }

    const data = blogSnap.data();
    return {
      id: blogSnap.id,
      title: data.title || '',
      summary: data.summary || '',
      image: data.image || '',
      date: data.date || null,
      author: data.author || '',
      readTime: data.readTime || '',
      category: data.category || '',
      content: data.content || '',
      tags: data.tags || [],
      views: data.views || 0
    };
  } catch (error) {
    console.error('Error fetching blog details:', error);
    throw error;
  }
};

// Optimized similar blogs fetching
const fetchSimilarBlogsOptimized = async (currentBlogId: string, category: string, limitCount = 4) => {
  try {
    let similarQuery = query(
      collection(db, 'blogs'),
      where('category', '==', category),
      orderBy('date', 'desc'),
      limit(limitCount + 1) // Get one extra to exclude current blog
    );

    const snapshot = await getDocs(similarQuery);
    
    // Filter out current blog and map only summary fields
    const similarBlogs = snapshot.docs
      .filter(doc => doc.id !== currentBlogId)
      .slice(0, limitCount)
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          summary: data.summary || '',
          image: data.image || '',
          date: data.date || null,
          author: data.author || '',
          readTime: data.readTime || '',
          category: data.category || ''
        };
      });

    return similarBlogs;
  } catch (error) {
    console.error('Error fetching similar blogs:', error);
    return [];
  }
};


// Helper to format Firestore Timestamp or string date
function formatDate(date: any): string {
  if (!date) return '';
  // If ISO string (YYYY-MM-DD), format as '10 Mar 2025'
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  if (typeof date === 'string') return date;
  if (date._seconds) {
    return new Date(date._seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  if (date.seconds) {
    return new Date(date.seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return date.toString();
}

// Helper to detect if a line is a quote
function isQuoteLine(line: string): boolean {
  // Check for various quote marks at the start of a line
  // This regex checks for: smart quotes (left/right), straight quotes, and other quote variants
  const isQuote = /^[""\u201C\u201D\u0022\u275D\u275E\u301D\u301E]/.test(line);
  return isQuote;
}

// Helper to detect if a line is an attribution (author line)
// Important: This should ONLY be used for lines that appear immediately after a quote!
function isAttributionLine(line: string): boolean {
  // Recognize any dash/hyphen followed by a name and optional title/role (not a bullet/list)
  // Accept: em dash, en dash, double hyphen, regular hyphen
  // Example: '- Juan Montoya, Interior Designer' or '-- Albert Einstein'
  const dashAuthorRegex = /^\s*(—|–|--|-|\u2014|\u2013)\s*([A-Z][a-zA-Z .'-]+)(,\s*[A-Za-z .'-]+)?$/;
  // Exclude lines that look like bullets: dash followed by lowercase or number
  // Accept if after dash, next char is uppercase and line is short
  return dashAuthorRegex.test(line.trim());
}

// Helper to detect if a line is a bullet point/list item
function isBulletPointLine(line: string): boolean {
  // Match lines that start with a hyphen (-), asterisk (*) or bullet (•) followed by a space
  // Make sure it's formatted as an actual list item, not just any line with a hyphen
  // We specifically look for a regular hyphen (-), not an em-dash (—) or en-dash (–)
  return /^\s*[-*•]\s+\w/.test(line) && !/^\s*(—|–|--)\s/.test(line);
}

// Helper to process text and apply highlighting for underscores and capital words
function processTextWithHighlighting(text: string): React.ReactNode {
  if (!text) return text;

  // First, split by underscores to handle _highlighted_ text
  const underscoreParts = text.split('_');
  const processedParts: React.ReactNode[] = [];

  for (let i = 0; i < underscoreParts.length; i++) {
    if (i % 2 === 0) {
      // Even indices are outside underscores - process for capital words
      if (underscoreParts[i]) {
        processedParts.push(processCapitalWords(underscoreParts[i]));
      }
    } else {
      // Odd indices are inside underscores - apply underscore highlighting
      if (underscoreParts[i]) {
        processedParts.push(
          <span key={`underscore-${i}`} className="highlighted-underscore">
            {processCapitalWords(underscoreParts[i])}
          </span>
        );
      }
    }
  }

  return processedParts.length > 1 ? processedParts : processCapitalWords(text);
}

// Helper to process capital words within text
function processCapitalWords(text: string): React.ReactNode {
  if (!text) return text;

  // Split by spaces to check each word
  const words = text.split(' ');
  const processedParts: React.ReactNode[] = [];
  let currentCapsGroup: string[] = [];
  let keyIndex = 0;

  const flushCapsGroup = () => {
    if (currentCapsGroup.length > 0) {
      processedParts.push(
        <span key={`caps-group-${keyIndex++}`} className="highlighted-caps">
          {currentCapsGroup.join(' ')}
        </span>
      );
      currentCapsGroup = [];
    }
  };

  const addRegularWord = (word: string) => {
    processedParts.push(word);
  };

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    
    // Check if word is all capitals (excluding punctuation and at least 2 letters)
    const lettersOnly = word.replace(/[^a-zA-Z]/g, '');
    const isAllCaps = lettersOnly.length >= 2 && lettersOnly === lettersOnly.toUpperCase() && /[A-Z]/.test(lettersOnly);
    
    if (isAllCaps) {
      // Add this word to the current caps group
      currentCapsGroup.push(word);
    } else {
      // Flush any pending caps group before adding regular word
      flushCapsGroup();
      addRegularWord(word);
    }
    
    // Add space between words (except for the last word)
    if (i < words.length - 1) {
      // If we're in a caps group, don't add the space yet (it will be included in the group)
      // If we're not in a caps group, add the space
      if (currentCapsGroup.length === 0) {
        processedParts.push(' ');
      }
    }
  }

  // Flush any remaining caps group
  flushCapsGroup();

  return processedParts.length > 1 ? processedParts : text;
}

// Helper to process [text](url) links, images, and clickable image backlinks
function processLinks(text: string): React.ReactNode {
  // Image markdown: ![alt|width=400|height=200](url) or ![](url)
  // Clickable image: [![](url)](link)
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  // Clickable image: [![](url)](link)
  const clickableImageRegex = /\[!\[([^\]]*)\]\(([^)]+)\)\]\(([^)]+)\)/g;
  let clickableMatch;
  while ((clickableMatch = clickableImageRegex.exec(text)) !== null) {
    if (clickableMatch.index > lastIndex) {
      parts.push(text.slice(lastIndex, clickableMatch.index));
    }
    // Parse alt and size from clickableMatch[1]
    let alt = clickableMatch[1] || '';
    let imgUrl = clickableMatch[2];
    let linkUrl = clickableMatch[3];
    let width, height;
    const sizeMatch = alt.match(/\|\s*width=(\d+)/);
    if (sizeMatch) width = sizeMatch[1];
    const heightMatch = alt.match(/\|\s*height=(\d+)/);
    if (heightMatch) height = heightMatch[1];
    alt = alt.split('|')[0].trim();
    parts.push(
      <a href={linkUrl} target="_blank" rel="noopener noreferrer">
        <img src={imgUrl} alt={alt} style={{ maxWidth: width ? `${width}px` : '400px', height: height ? `${height}px` : 'auto', borderRadius: '8px', margin: '12px 0', display: 'block' }} />
      </a>
    );
    lastIndex = clickableImageRegex.lastIndex;
  }

  // Process remaining text for images
  let remaining = text.slice(lastIndex);
  lastIndex = 0;
  while ((match = imageRegex.exec(remaining)) !== null) {
    if (match.index > lastIndex) {
      parts.push(remaining.slice(lastIndex, match.index));
    }
    // Parse alt and size from match[1]
    let alt = match[1] || '';
    let imgUrl = match[2];
    let width, height;
    const sizeMatch = alt.match(/\|\s*width=(\d+)/);
    if (sizeMatch) width = sizeMatch[1];
    const heightMatch = alt.match(/\|\s*height=(\d+)/);
    if (heightMatch) height = heightMatch[1];
    alt = alt.split('|')[0].trim();
    parts.push(
      <img src={imgUrl} alt={alt} style={{ maxWidth: width ? `${width}px` : '400px', height: height ? `${height}px` : 'auto', borderRadius: '8px', margin: '12px 0', display: 'block' }} />
    );
    lastIndex = imageRegex.lastIndex;
  }

  // Process remaining text for links
  remaining = remaining.slice(lastIndex);
  lastIndex = 0;
  while ((match = linkRegex.exec(remaining)) !== null) {
    if (match.index > lastIndex) {
      parts.push(remaining.slice(lastIndex, match.index));
    }
    parts.push(
      <a href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#06c', textDecoration: 'none' }}>
        {match[1]}
      </a>
    );
    lastIndex = linkRegex.lastIndex;
  }
  if (lastIndex < remaining.length) {
    parts.push(remaining.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

const BlogSummaryTile: React.FC<{ blog: BlogSummary }> = ({ blog }) => (
  <div className="blog-summary-tile" style={{ marginBottom: 0 }}>
    <div className="blog-summary-image-wrapper">
      <img className="blog-summary-image" src={blog.image} alt={blog.category} />
      <span className="blog-summary-category">{blog.category}</span>
    </div>
    <div className="blog-summary-content">
      <div className="blog-summary-title">{blog.title}</div>
      <div className="blog-summary-author">By {blog.author}</div>
      <svg className="blog-summary-divider" width="333" height="2" viewBox="0 0 333 2" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 1H333" stroke="#E5E7EB"/>
      </svg>
      <div className="blog-summary-meta">
        <span>{formatDate(blog.date)}</span>
        <span className="blog-summary-dot">•</span>
        <span>{blog.readTime}</span>
      </div>
    </div>
  </div>
);


const BlogDetailsPage: React.FC = () => {
  const { blogId } = useParams<{ blogId: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<(BlogSummary & { content?: string }) | null>(null);
  const [similarBlogs, setSimilarBlogs] = useState<BlogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const blogIdsRef = useRef<string[]>([]); // cache blog IDs for navigation
  const [categories, setCategories] = useState<string[]>(['All']);
  const [categoryLoading, setCategoryLoading] = useState<string | null>(null);

  // Session-based caching refs
  const blogCacheRef = useRef<Map<string, { blog: BlogSummary & { content?: string }, timestamp: number }>>(new Map());
  const similarCacheRef = useRef<Map<string, { similar: BlogSummary[], timestamp: number }>>(new Map());
  const blogIdsCacheRef = useRef<{ ids: string[], timestamp: number } | null>(null);

  // Cache duration: 1 hour (3600000 ms)
  const CACHE_DURATION = 3600000;

  // Fetch unique categories
  useEffect(() => {
    const fetchCategories = async () => {
      const categoriesQuery = query(collection(db, 'blogs'));
      const snapshot = await getDocs(categoriesQuery);
      const uniqueCategories = new Set<string>();
      snapshot.docs.forEach(doc => {
        const category = doc.data().category;
        if (category) uniqueCategories.add(category);
      });
      setCategories(['All', ...Array.from(uniqueCategories).sort()]);
    };
    fetchCategories();
  }, []);

  // Handler for category selection (navigate to another recent blog of that category)
  const handleCategorySelect = async (category: string) => {
    if (category === 'All') {
      navigate('/blogs');
      return;
    }
    
    setCategoryLoading(category); // Set loading state for this category
    
    try {
      // Check cache first for blogs of this category
      const cacheKey = `category_blogs_${category}`;
      const cached = sessionStorage.getItem(cacheKey);
      let categoryBlogs: any[] = [];
      
      if (cached) {
        const cachedData = JSON.parse(cached);
        if (Date.now() - cachedData.timestamp < 300000) { // 5 minutes cache
          categoryBlogs = cachedData.blogs;
        }
      }
      
      // If no cache or expired, fetch from Firestore
      if (categoryBlogs.length === 0) {
        const categoryQuery = query(
          collection(db, 'blogs'),
          where('category', '==', category),
          orderBy('createdAt', 'desc'),
          limit(5) // Get top 5 recent blogs
        );
        
        const snapshot = await getDocs(categoryQuery);
        categoryBlogs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Cache the results
        sessionStorage.setItem(cacheKey, JSON.stringify({
          blogs: categoryBlogs,
          timestamp: Date.now()
        }));
      }
      
      // Find a blog different from current one
      const otherBlog = categoryBlogs.find((blogItem: any) => blogItem.id !== blogId);
      
      if (otherBlog) {
        // Navigate to another blog in the same category
        navigate(`/blogs/${otherBlog.id}`);
      } else {
        // If no other blog available, fallback to category page
        navigate(`/blogs?category=${encodeURIComponent(category)}`);
      }
    } catch (error) {
      console.error('Error fetching category blogs:', error);
      // Fallback to category page on error
      navigate(`/blogs?category=${encodeURIComponent(category)}`);
    } finally {
      setCategoryLoading(null); // Clear loading state
    }
  };

  // Optimized blog details fetching using Cloud Function
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    
    const fetchBlogDetails = async () => {
      if (!blogId) return;
      
      try {
        const now = Date.now();
        
        // Check blog cache first
        const cachedBlog = blogCacheRef.current.get(blogId);
        if (cachedBlog && (now - cachedBlog.timestamp) < CACHE_DURATION) {
          console.log('BlogDetails: Using cached blog data for:', blogId);
          if (isMounted) setBlog(cachedBlog.blog);
        } else {
          console.log('BlogDetails: Fetching fresh blog data using optimized direct method for:', blogId);
          
          try {
            // Use optimized direct Firestore for blog details
            const blogData = await fetchBlogDetailsOptimized(blogId);
            
            if (!blogData) {
              if (isMounted) setBlog(null);
              return;
            }
            
            const blogDetail: BlogSummary & { content?: string } = {
              id: blogData.id,
              image: blogData.image ?? '',
              category: blogData.category ?? '',
              title: blogData.title ?? '',
              author: blogData.author ?? '',
              date: blogData.date ?? '',
              readTime: blogData.readTime ?? '',
              summary: blogData.summary ?? '',
              content: blogData.content ?? '',
            };
            
            setBlog(blogDetail);
            blogCacheRef.current.set(blogId, { blog: blogDetail, timestamp: now });
            
            // Fetch similar blogs
            const similarBlogs = await fetchSimilarBlogsOptimized(blogId, blogData.category);
            if (similarBlogs && Array.isArray(similarBlogs)) {
              setSimilarBlogs(similarBlogs);
              similarCacheRef.current.set(blogId, { similar: similarBlogs, timestamp: now });
            }
            
          } catch (cloudFunctionError) {
            console.error('Cloud Function error, falling back to direct Firestore:', cloudFunctionError);
            
            // Fallback to direct Firestore if Cloud Function fails
            const { db } = await import('../../config/firebase-config');
            const { doc, getDoc } = await import('firebase/firestore');
            
            const blogDocRef = doc(db, 'blogs', blogId);
            const blogSnap = await getDoc(blogDocRef);
            
            if (!blogSnap.exists()) {
              if (isMounted) setBlog(null);
              return;
            }
            
            const data = blogSnap.data();
            const blogDetail: BlogSummary & { content?: string } = {
              id: blogSnap.id,
              image: data.image ?? '',
              category: data.category ?? '',
              title: data.title ?? '',
              author: data.author ?? '',
              date: data.date ?? '',
              readTime: data.readTime ?? '',
              summary: data.summary ?? '',
              content: data.content ?? '',
            };
            
            setBlog(blogDetail);
            blogCacheRef.current.set(blogId, { blog: blogDetail, timestamp: now });
          }
        }

        // Ensure we have blog IDs for navigation (fallback if not from Cloud Function)
        if (blogIdsRef.current.length === 0) {
          const cachedIds = blogIdsCacheRef.current;
          if (!cachedIds || (now - cachedIds.timestamp) > CACHE_DURATION) {
            const { db } = await import('../../config/firebase-config');
            const { collection, query, orderBy, getDocs } = await import('firebase/firestore');
            
            const idsQuery = query(collection(db, 'blogs'), orderBy('date', 'desc'));
            const idsSnap = await getDocs(idsQuery);
            const blogIds = idsSnap.docs.map(doc => doc.id);
            
            blogIdsCacheRef.current = { ids: blogIds, timestamp: now };
            blogIdsRef.current = blogIds;
          } else {
            blogIdsRef.current = cachedIds.ids;
          }
        }
        
      } catch (err) {
        console.error('Error fetching blog details:', err);
        if (isMounted) setBlog(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    fetchBlogDetails();
    return () => { isMounted = false; };
  }, [blogId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '500px', color: '#1F2937' }}>
      <div>
        <div style={{ marginBottom: '18px', fontSize: '1.15rem', color: '#444', fontWeight: 500 }}>
          Sketching ideas and setting the scene
        </div>
        </div>
        <Loader />
      </div>
    );
  }
  if (!blog) {
    return <div>Blog not found.</div>;
  }

  // Get all blog IDs in order (from cache)
  const blogIds = blogIdsRef.current;
  const currentIndex = blogIds.indexOf(blogId || '');
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === blogIds.length - 1;
  const nextBlogId = !isLast && currentIndex !== -1 ? blogIds[currentIndex + 1] : null;
  const prevBlogId = !isFirst && currentIndex !== -1 ? blogIds[currentIndex - 1] : null;

  return (
    <>
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{blog.title} | Kydoscope Blog</title>
        <meta name="description" content={blog.summary || `Read about ${blog.title} - ${blog.category} insights and tips on Kydoscope design blog.`} />
        <meta name="keywords" content={`${blog.category}, ${blog.title}, interior design, architecture, design tips, ${blog.author}`} />
        <meta name="author" content={blog.author} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={`${blog.title} | Kydoscope Blog`} />
        <meta property="og:description" content={blog.summary || `Read about ${blog.title} - ${blog.category} insights and tips.`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://kydoscope.com/blogs/${blogId}`} />
        <meta property="og:image" content={blog.image} />
        <meta property="article:author" content={blog.author} />
        <meta property="article:published_time" content={blog.date} />
        <meta property="article:section" content={blog.category} />
        <meta property="article:tag" content={blog.category} />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${blog.title} | Kydoscope Blog`} />
        <meta name="twitter:description" content={blog.summary || `Read about ${blog.title} - ${blog.category} insights and tips.`} />
        <meta name="twitter:image" content={blog.image} />
        
        {/* Additional SEO */}
        <link rel="canonical" href={`https://kydoscope.com/blogs/${blogId}`} />
        <meta name="robots" content="index, follow" />
        <meta name="article:tag" content={blog.category} />
        
        {/* Structured Data for Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": blog.title,
            "description": blog.summary || `Read about ${blog.title} - ${blog.category} insights and tips.`,
            "image": blog.image,
            "author": {
              "@type": "Person",
              "name": blog.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Kydoscope",
              "logo": {
                "@type": "ImageObject",
                "url": "https://kydoscope.com/logo512.png"
              }
            },
            "datePublished": blog.date,
            "dateModified": blog.date,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://kydoscope.com/blogs/${blogId}`
            },
            "articleSection": blog.category,
            "keywords": `${blog.category}, interior design, architecture, design tips`,
            "url": `https://kydoscope.com/blogs/${blogId}`
          })}
        </script>
      </Helmet>

      <div className="blog-details-layout">
        <div className="blog-details-left">
          <BlogSummaryTile blog={blog} />
          
          {/* Categories Section */}
          <div className="blog-details-categories-section">
            <h3 className="blog-details-categories-title">Explore More Articles</h3>
            <ul className="blog-categories-list blog-details-categories">
              {categories.map(category => (
                <li key={category} className="blog-category-list-item">
                  <button
                    className={`blog-category-btn ${blog.category === category && category !== 'All' ? 'active' : ''}`}
                    onClick={() => handleCategorySelect(category)}
                    disabled={categoryLoading === category}
                    style={{
                      background: blog.category === category && category !== 'All' ? '#1F2937' : '#F9FAFB',
                      color: blog.category === category && category !== 'All' ? '#fff' : '#1F2937',
                      border: blog.category === category && category !== 'All' ? '1px solid #1F2937' : '1px solid #E5E7EB',
                      opacity: categoryLoading === category ? 0.6 : 1,
                      cursor: categoryLoading === category ? 'wait' : 'pointer'
                    }}
                  >
                    {categoryLoading === category ? 'Loading...' : category}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          {similarBlogs.length > 0 && (
            <div className="similar-blogs-section">
              <h3 className="similar-blogs-title">Similar Blogs</h3>
              <div className="similar-blogs-list">
                {similarBlogs.map(similar => (
                  <div
                    key={similar.id}
                    className="similar-blog-block"
                    onClick={() => navigate(`/blogs/${similar.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="similar-blog-image-wrapper">
                      <img className="similar-blog-image" src={similar.image} alt={similar.category} />
                    </div>
                    <div className="similar-blog-info">
                      <div className="similar-blog-title">{similar.title}</div>
                      <div className="similar-blog-author">By {similar.author}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="blog-details-right">
          <h2 className="blog-details-title">{blog.title}</h2>
          <div className="blog-details-content">
            {(() => {
              const lines = blog.content?.split('\n') || [];
              const elements = [];
              let inList = false;
              let listItems: JSX.Element[] = [];
              for (let idx = 0; idx < lines.length; idx++) {
                let trimmed = lines[idx].trim();
                // Bold the part before colon for 'Text: value' or 'Text : value' lines
                const colonRegex = /^(.+?)\s*:\s*(.*)$/;
                const colonMatch = trimmed.match(colonRegex);
                // Only apply colon logic if line does NOT contain a markdown link
                if (colonMatch && !/\[[^\]]+\]\([^)]*\)/.test(trimmed)) {
                  const beforeColon = colonMatch[1];
                  const afterColon = colonMatch[2];
                  elements.push(
                    <p key={idx} className="blog-paragraph">
                      <span style={{ fontWeight: 600 , color: '#1F2937' }}>{beforeColon}:</span>{afterColon ? ' ' : null}{processLinks(afterColon)}
                    </p>
                  );
                  continue;
                }
                // ...rest of your logic, do not redeclare 'trimmed' again in this loop...
                
                // Skip empty lines but preserve spacing between sections
                if (!trimmed) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  continue;
                }
                
                // Check for title first (first line is usually the title)
                if (idx === 0 && elements.length === 0 && trimmed.length > 10) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h2 className="blog-main-title" key={idx}>{processLinks(trimmed)}</h2>);
                  continue;
                }

                // Highlight blockquotes (lines starting with any quote mark)
                // NOTE: We're checking for quotes BEFORE checking for headings to avoid confusion
                if (isQuoteLine(trimmed)) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  
                  // Check if next line is an author (starts with em dash or similar)
                  const nextLine = lines[idx + 1]?.trim();
                  if (nextLine && isAttributionLine(nextLine)) {
                    // This is a quote with attribution
                    elements.push(
                      <blockquote key={idx} className="blog-quote">
                        {processLinks(trimmed)}
                        <div className="blog-quote-author">{processLinks(nextLine)}</div>
                      </blockquote>
                    );
                    idx++; // Skip the author line
                    continue;
                  } else {
                    // This is a quote without attribution
                    elements.push(<blockquote key={idx} className="blog-quote">{processLinks(trimmed)}</blockquote>);
                    continue;
                  }
                }
                
                // All uppercase lines as headings
                if (/^[A-Z][A-Z\s—-]+$/.test(trimmed) && trimmed.length > 6) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h3 className="blog-heading" key={idx}>{processLinks(trimmed)}</h3>);
                  continue;
                }

                // Numbered headings like '2. Space-Saving Layouts'
                if (/^\d+\.\s*[A-Z][a-zA-Z\s-]+$/.test(trimmed)) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h3 className="blog-heading" key={idx}>{processLinks(trimmed)}</h3>);
                  continue;
                }

                // Title Case detection: treat as heading if most words start with uppercase
                // and it's a short phrase (less than 60 chars)
                const words = trimmed.split(' ');
                const titleCaseCount = words.filter((w: string) => w.length > 2 && /^[A-Z][a-z]/.test(w)).length;
                if (words.length >= 2 && 
                    words.length <= 8 && 
                    titleCaseCount / words.length > 0.5 && 
                    !trimmed.includes('.') && 
                    trimmed.length < 60) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h3 className="blog-heading" key={idx}>{processLinks(trimmed)}</h3>);
                  continue;
                }
                if (isQuoteLine(trimmed)) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  // Check if next line is an author attribution
                  const nextLine = lines[idx + 1]?.trim();
                  if (nextLine && isAttributionLine(nextLine)) {
                    elements.push(
                      <blockquote key={idx} className="blog-quote">
                        {processLinks(trimmed)}
                        <div className="blog-quote-author">{processLinks(nextLine)}</div>
                      </blockquote>
                    );
                    idx++; // Skip the author line
                    continue;
                  } else {
                    elements.push(<blockquote key={idx} className="blog-quote">{processLinks(trimmed)}</blockquote>);
                    continue;
                  }
                }
                
                // We've removed standalone attribution handling - only handle attributions after quotes
                
                // We don't need special handling for regular dashes at paragraph start anymore
                // That's handled by the general paragraph case
                
                // Check for CTA section
                if (trimmed.startsWith('Need Help Turning Vision Into Reality?')) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  // Start a CTA section
                  const ctaElements = [<h3 key={`cta-title-${idx}`} className="blog-cta-title">{processLinks(trimmed)}</h3>];
                  // Collect the next few lines for the CTA
                  let j = idx + 1;
                  while (j < lines.length && ctaElements.length < 6) {
                    const ctaLine = lines[j].trim();
                    if (ctaLine) {
                      ctaElements.push(<p key={`cta-p-${j}`} className="blog-cta-paragraph">{processLinks(ctaLine)}</p>);
                    }
                    j++;
                  }
                  // Add the entire CTA section
                  elements.push(
                    <div key={`cta-${idx}`} className="blog-cta">
                      {ctaElements}
                    </div>
                  );
                  // Skip the lines we've already processed
                  idx = j - 1;
                  continue;
                }

                // Handle list items - collect them into a proper <ul>
                // Using our helper to detect bullet points
                if (isBulletPointLine(trimmed)) {
                  // This is a bullet point (not an attribution)
                  inList = true;
                  const cleanedText = trimmed.replace(/^\s*[-*•]\s+/, '');
                  listItems.push(
                    <li key={`item-${idx}`} className="blog-list-item">
                      {processLinks(cleanedText)}
                    </li>
                  );
                  continue;
                } else if (inList) {
                  // End of a list, add the collected items
                  elements.push(<ul key={`list-${idx}`} className="blog-list">{listItems}</ul>);
                  listItems = [];
                  inList = false;
                }
                
                // Render as paragraph otherwise
                elements.push(<p className="blog-paragraph" key={idx}>{processLinks(trimmed)}</p>);
              }
              
              // Add any remaining list items
              if (inList && listItems.length > 0) {
                elements.push(<ul key="final-list" className="blog-list">{listItems}</ul>);
              }
              return elements;
            })()}
          </div>
        </div>
      </div>
      <div className="blog-details-nav-btns">
        <button
          className="blog-nav-btn blog-nav-btn-prev"
          onClick={() => prevBlogId && navigate(`/blogs/${prevBlogId}`)}
          disabled={!prevBlogId}
          style={{ opacity: !prevBlogId ? 0.5 : 1, cursor: !prevBlogId ? 'not-allowed' : 'pointer' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '2px' }}>
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Previous
        </button>
        <button
          className="blog-nav-btn blog-nav-btn-next"
          onClick={() => nextBlogId && navigate(`/blogs/${nextBlogId}`)}
          disabled={!nextBlogId}
          style={{ opacity: !nextBlogId ? 0.5 : 1, cursor: !nextBlogId ? 'not-allowed' : 'pointer' }}
        >
          Next
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '2px' }}>
            <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </>
  );
};

export default BlogDetailsPage;
